import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, Download, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { getAnalytics } from '../../utils/adminApi';

// ── Date / cost helpers ───────────────────────────────────────────────────────

function parseUtc(str) {
  if (!str) return new Date(0);
  return new Date(str.endsWith('Z') || str.includes('+') ? str : str + 'Z');
}

function fmtDate(str) {
  return parseUtc(str).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtDateShort(str) {
  if (!str) return '—';
  // Append T12:00:00 so date-only strings parse as local noon, not UTC midnight
  // (avoids off-by-one-day display near timezone boundaries)
  const d = new Date(str.length === 10 ? str + 'T12:00:00' : str);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function fmtUSD(v) {
  if (v == null) return '—';
  // Always show 6 significant decimal places — costs are tiny per query
  return `$${v.toFixed(6)}`;
}

function fmtINR(v) {
  if (v == null) return '—';
  if (v < 0.01) return `₹${v.toFixed(6)}`;
  if (v < 1)    return `₹${v.toFixed(4)}`;
  return `₹${v.toFixed(2)}`;
}

// ── CSV export ────────────────────────────────────────────────────────────────

function downloadCSV(data) {
  const esc = v => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const cols = ['Tenant', 'User', 'Query', 'Module', 'Status', 'Tokens', 'Cost USD', 'Cost INR', 'Response ms', 'When (local)'];
  const rows = data.recent_queries.map(q => [
    q.tenant_id, q.user_email, q.query_text, q.sap_module || '',
    q.status, q.token_count || 0,
    q.cost_usd != null ? q.cost_usd.toFixed(6) : '',
    q.cost_inr != null ? q.cost_inr.toFixed(4)  : '',
    q.response_time_ms || '', fmtDate(q.created_at),
  ].map(esc).join(','));
  const csv  = ['﻿' + cols.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `querely-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Style constants ───────────────────────────────────────────────────────────

const MODULE_COLORS = {
  MM: 'var(--clr-mm)', SD: 'var(--clr-sd)', FI: 'var(--clr-fi)',
  PP: 'var(--clr-pp)', ITSM: 'var(--clr-itsm)', EWM: 'var(--clr-info)',
};
const STATUS_CLASS = { success: 'active', failed: 'inactive', fallback: 'warning', rejected: 'inactive' };

// ── Filter bar ────────────────────────────────────────────────────────────────

const selStyle = {
  padding: '4px 8px', background: 'var(--clr-bg-elevated)',
  border: '1px solid var(--clr-border)', borderRadius: 'var(--radius-sm)',
  color: 'var(--clr-text-primary)', fontSize: '0.78rem', cursor: 'pointer',
};

function FilterBar({ filters, onChange, onClear, tenantOptions, moduleOptions }) {
  return (
    <div style={{
      display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
      padding: '10px 12px', background: 'var(--clr-bg-surface)',
      border: '1px solid var(--clr-border-soft)', borderRadius: 'var(--radius-md)',
      marginBottom: 12,
    }}>
      <Filter size={13} style={{ color: 'var(--clr-text-muted)', flexShrink: 0 }} />

      <select
        value={filters.tenant}
        onChange={e => onChange('tenant', e.target.value)}
        style={selStyle}
      >
        <option value="">All Tenants</option>
        {tenantOptions.map(t => <option key={t} value={t}>{t}</option>)}
      </select>

      <select
        value={filters.module}
        onChange={e => onChange('module', e.target.value)}
        style={selStyle}
      >
        <option value="">All Modules</option>
        {moduleOptions.map(m => <option key={m} value={m}>{m}</option>)}
      </select>

      <select
        value={filters.status}
        onChange={e => onChange('status', e.target.value)}
        style={selStyle}
      >
        <option value="">All Statuses</option>
        <option value="success">success</option>
        <option value="failed">failed</option>
        <option value="fallback">fallback</option>
        <option value="rejected">rejected</option>
      </select>

      <input
        type="date"
        value={filters.dateFrom}
        onChange={e => onChange('dateFrom', e.target.value)}
        style={selStyle}
        placeholder="From"
        title="From date"
      />
      <input
        type="date"
        value={filters.dateTo}
        onChange={e => onChange('dateTo', e.target.value)}
        style={selStyle}
        placeholder="To"
        title="To date"
      />

      {(filters.tenant || filters.module || filters.status || filters.dateFrom || filters.dateTo) && (
        <button
          onClick={onClear}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '3px 8px', background: 'var(--clr-error-bg)',
            border: '1px solid var(--clr-error)', borderRadius: 'var(--radius-sm)',
            color: 'var(--clr-error)', fontSize: '0.75rem', cursor: 'pointer',
          }}
        >
          <X size={11} /> Clear
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { tenant: '', module: '', status: '', dateFrom: '', dateTo: '' };

export default function AnalyticsPage() {
  const { adminKey }              = useAdminAuth();
  const [data, setData]           = useState(null);
  const [error, setError]         = useState('');
  const [filters, setFilters]     = useState(EMPTY_FILTERS);
  const [page, setPage]           = useState(1);
  const PAGE_SIZE = 50;

  // debounce filter changes — avoid hammering API on every keystroke
  const debounceRef = useRef(null);

  const load = useCallback((f = filters, pg = page) => {
    getAnalytics(adminKey, {
      tenant:   f.tenant   || undefined,
      module:   f.module   || undefined,
      status:   f.status   || undefined,
      dateFrom: f.dateFrom || undefined,
      dateTo:   f.dateTo   || undefined,
      page:     pg,
      pageSize: PAGE_SIZE,
    })
      .then(d => { setError(''); setData(d); })
      .catch(err => setError(err.message));
  }, [adminKey, filters, page]);

  // initial load
  useEffect(() => { load(EMPTY_FILTERS, 1); }, [adminKey]);

  const handleFilterChange = (key, val) => {
    const next = { ...filters, [key]: val };
    setFilters(next);
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(next, 1), 350);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    load(EMPTY_FILTERS, 1);
  };

  const handlePageChange = (pg) => {
    setPage(pg);
    load(filters, pg);
  };

  if (error) return <div className="admin-error-page">{error}</div>;
  if (!data)  return <div className="admin-page" style={{ color: 'var(--clr-text-muted)' }}>Loading…</div>;

  // ── Aggregates ──────────────────────────────────────────────────────────────
  const totalQueries = data.tenant_stats.reduce((s, t) => s + t.total_queries, 0);
  const totalTokens  = data.tenant_stats.reduce((s, t) => s + t.total_tokens,  0);
  const totalErrors  = data.tenant_stats.reduce((s, t) => s + t.errors,        0);
  const totalCostUSD = data.tenant_stats.reduce((s, t) => s + (t.cost_usd || 0), 0);
  const totalCostINR = totalCostUSD * 84;

  // derive filter options from global data
  const tenantOptions = [...new Set(data.tenant_stats.map(t => t.tenant_id))];
  const moduleOptions = [...new Set(data.module_breakdown.map(m => m.module))];

  const totalPages    = Math.ceil((data.recent_total || 0) / PAGE_SIZE);
  const hasFilters    = Object.values(filters).some(Boolean);

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Analytics</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="admin-btn-icon" title="Download CSV" onClick={() => downloadCSV(data)}><Download size={15} /></button>
          <button className="admin-btn-icon" title="Refresh" onClick={() => load()}><RefreshCw size={15} /></button>
        </div>
      </div>

      {/* ── Top totals ── */}
      <div className="admin-stat-grid" style={{ marginBottom: 32 }}>
        <StatCard value={totalQueries.toLocaleString()} label="Total Queries" />
        <StatCard value={totalTokens.toLocaleString()} label="Total Tokens Used" />
        <StatCard
          value={fmtINR(totalCostINR)}
          sub={fmtUSD(totalCostUSD)}
          label="Est. Total Cost"
        />
        <StatCard
          value={totalErrors}
          label="Total Errors"
          valueStyle={{ color: totalErrors > 0 ? 'var(--clr-error)' : undefined }}
        />
        <StatCard value={data.tenant_stats.length} label="Active Tenants" />
      </div>

      {/* ── Usage per Tenant ── */}
      <h3 className="admin-section-title" style={{ marginTop: 0 }}>Usage per Tenant</h3>
      <div className="admin-table-wrap" style={{ marginBottom: 32 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tenant</th><th>Queries</th><th>Tokens</th>
              <th>Est. Cost (INR)</th><th>Est. Cost (USD)</th>
              <th>Errors</th><th>Error %</th><th>Avg Response</th>
            </tr>
          </thead>
          <tbody>
            {data.tenant_stats.length === 0 && (
              <tr><td colSpan={8} className="admin-table-empty">No query data yet</td></tr>
            )}
            {data.tenant_stats.map(t => (
              <tr key={t.tenant_id}>
                <td><code>{t.tenant_id}</code></td>
                <td>{t.total_queries.toLocaleString()}</td>
                <td>{t.total_tokens.toLocaleString()}</td>
                <td style={{ color: 'var(--clr-success)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtINR(t.cost_inr)}
                </td>
                <td style={{ color: 'var(--clr-text-muted)', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtUSD(t.cost_usd)}
                </td>
                <td style={{ color: t.errors > 0 ? 'var(--clr-error)' : undefined }}>{t.errors}</td>
                <td>{t.total_queries > 0 ? `${((t.errors / t.total_queries) * 100).toFixed(1)}%` : '—'}</td>
                <td>{t.avg_response_ms ? `${t.avg_response_ms} ms` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Cost by Date ── */}
      <h3 className="admin-section-title">Cost by Date (last 30 days)</h3>
      <div className="admin-table-wrap" style={{ marginBottom: 32 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th><th>Queries</th><th>Tokens</th><th>Est. Cost (INR)</th><th>Est. Cost (USD)</th>
            </tr>
          </thead>
          <tbody>
            {data.daily_breakdown.length === 0 && (
              <tr><td colSpan={5} className="admin-table-empty">No data yet</td></tr>
            )}
            {data.daily_breakdown.map(d => (
              <tr key={d.date}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDateShort(d.date)}</td>
                <td>{d.queries.toLocaleString()}</td>
                <td>{d.tokens.toLocaleString()}</td>
                <td style={{ color: 'var(--clr-success)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtINR(d.cost_inr)}
                </td>
                <td style={{ color: 'var(--clr-text-muted)', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtUSD(d.cost_usd)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Queries by SAP Module ── */}
      <h3 className="admin-section-title">Queries by SAP Module</h3>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
        {data.module_breakdown.length === 0 && (
          <span style={{ color: 'var(--clr-text-muted)', fontSize: '0.875rem' }}>No data yet</span>
        )}
        {data.module_breakdown.map(m => (
          <div key={m.module} className="admin-stat-card" style={{ minWidth: 120, padding: '12px 16px' }}>
            <div>
              <div className="admin-stat-value" style={{ color: MODULE_COLORS[m.module] || 'var(--clr-text-primary)', fontSize: '1.3rem' }}>
                {m.count}
              </div>
              <div className="admin-stat-label">{m.module}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Recent Queries ── */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <h3 className="admin-section-title" style={{ margin: 0 }}>Recent Queries</h3>
        <span style={{ fontSize: '0.78rem', color: 'var(--clr-text-muted)' }}>
          {data.recent_total?.toLocaleString() ?? 0} total
          {hasFilters && <span style={{ color: 'var(--clr-accent-light)' }}> (filtered)</span>}
        </span>
      </div>

      <FilterBar
        filters={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        tenantOptions={tenantOptions}
        moduleOptions={moduleOptions}
      />

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Tenant</th><th>User</th><th>Query</th>
              <th>Module</th><th>Status</th><th>Tokens</th>
              <th>Est. Cost (INR)</th><th>Time</th><th>When</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_queries.length === 0 && (
              <tr><td colSpan={9} className="admin-table-empty">No queries match the current filters</td></tr>
            )}
            {data.recent_queries.map(q => (
              <tr key={q.id}>
                <td><code>{q.tenant_id}</code></td>
                <td style={{ fontSize: '0.78rem', color: 'var(--clr-text-secondary)' }}>{q.user_email}</td>
                <td
                  style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem' }}
                  title={q.query_text}
                >
                  {q.query_text}
                </td>
                <td>
                  {q.sap_module
                    ? <span className="admin-tag" style={{ color: MODULE_COLORS[q.sap_module] }}>{q.sap_module}</span>
                    : <span className="admin-muted">—</span>}
                </td>
                <td><span className={`admin-badge ${STATUS_CLASS[q.status] || 'active'}`}>{q.status}</span></td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{q.token_count?.toLocaleString() || '—'}</td>
                <td style={{ color: 'var(--clr-success)', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {q.cost_inr != null ? fmtINR(q.cost_inr) : '—'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{q.response_time_ms ? `${q.response_time_ms}ms` : '—'}</td>
                <td style={{ fontSize: '0.78rem', color: 'var(--clr-text-secondary)', whiteSpace: 'nowrap' }}>
                  {fmtDate(q.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            className="admin-btn-icon"
            disabled={page <= 1}
            onClick={() => handlePageChange(page - 1)}
          >
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: '0.8rem', color: 'var(--clr-text-secondary)' }}>
            Page {page} of {totalPages}
          </span>
          <button
            className="admin-btn-icon"
            disabled={page >= totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Stat card helper ──────────────────────────────────────────────────────────

function StatCard({ value, sub, label, valueStyle }) {
  return (
    <div className="admin-stat-card">
      <div>
        <div className="admin-stat-value" style={valueStyle}>{value}</div>
        {sub && <div style={{ fontSize: '0.72rem', color: 'var(--clr-text-muted)', marginTop: 1 }}>{sub}</div>}
        <div className="admin-stat-label">{label}</div>
      </div>
    </div>
  );
}
